import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Clock, Pencil, Plus } from "lucide-react";
import { Suspense, useState } from "react";
import { ApiError } from "@/api/errors";
import { type CatalogService, deleteService, servicesQuery } from "@/api/services";
import { warmQueries } from "@/api/warm";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageActions } from "@/components/page-actions";
import { ServicoDialog } from "@/components/ServicoDialog";
import { ServicosInativos } from "@/components/servicos/ServicosInativos";
import { Button } from "@/components/ui/button";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/configuracoes/servicos")({
  loader: ({ context: { queryClient, establishmentId } }) =>
    warmQueries(queryClient, [servicesQuery(establishmentId)]),
  staticData: {
    title: "Serviços",
    subtitle: "Esta lista alimenta a agenda e o bot do WhatsApp.",
  },
  component: ServicosPage,
});

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function ServicosPage() {
  const { user } = useAuth();
  // `_authed` só renderiza autenticado — user nunca é null aqui.
  if (!user) return null;
  return (
    <ErrorBoundary titulo="Não foi possível carregar os serviços">
      <Suspense fallback={<ServicosSkeleton />}>
        <ServicosContent establishmentId={user.establishment_id} />
      </Suspense>
    </ErrorBoundary>
  );
}

function ServicosSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-72 w-full" />
    </div>
  );
}

function ServicosContent({ establishmentId }: { establishmentId: string }) {
  const queryClient = useQueryClient();
  const { data: services } = useSuspenseQuery(servicesQuery(establishmentId));

  // Diálogo único pros dois fluxos: `editing` preenchido = edição, null =
  // criação. O estado mora aqui pra o botão "Editar" de cada linha reabrir o
  // mesmo dialog com o serviço daquela linha.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogService | undefined>(undefined);

  // Desativar pede confirmação: o serviço some das opções do bot na hora, e
  // um clique acidental no switch tirava do ar o que o salão vende sem dizer
  // nada. A volta é pela aba "Inativos" (activateService), não pelo switch.
  const [confirmingOff, setConfirmingOff] = useState<CatalogService | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [tab, setTab] = useState<"ativos" | "inativos">("ativos");

  async function onDeactivate() {
    const service = confirmingOff;
    if (!service) return;
    setToggleError(null);
    setTogglingId(service.id);
    try {
      await deleteService(service.id);
      setConfirmingOff(null);
      await queryClient.invalidateQueries({
        queryKey: ["establishment", establishmentId, "services"],
      });
    } catch (e) {
      setToggleError(
        e instanceof ApiError
          ? e.message
          : "Não foi possível desativar. Tente de novo.",
      );
    } finally {
      setTogglingId(null);
    }
  }

  const actives = services.filter((s) => s.active);
  const inactives = services.filter((s) => !s.active);
  // Ticket médio só dos ativos — inativo não entra mais em agendamento novo.
  const avgTicketCents =
    actives.length === 0
      ? 0
      : actives.reduce((sum, s) => sum + s.price_cents, 0) / actives.length;
  // Mesmo escopo do ticket médio: inativo não entra em agendamento novo, então
  // não faz sentido contar o sinal dele no stat.
  const depositCount = actives.filter((s) => s.deposit_required).length;

  return (
    <>
      <PageActions>
        <Button
          size="sm"
          className="gap-1.5"
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Novo<span className="hidden sm:inline"> Serviço</span>
        </Button>
      </PageActions>

      <div>
        <div>
          {/* Stats */}
          <div className="grid grid-cols-1 divide-y rounded-md border bg-card sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <div className="px-4 py-3">
              <p className="text-xs text-muted-foreground">Serviços ativos</p>
              <p className="mt-0.5 text-lg font-semibold">{actives.length}</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-xs text-muted-foreground">Ticket médio</p>
              <p className="mt-0.5 text-lg font-semibold">
                {brl.format(avgTicketCents / 100)}
              </p>
            </div>
            <div className="px-4 py-3">
              <p className="text-xs text-muted-foreground">Exigem sinal</p>
              <p className="mt-0.5 text-lg font-semibold">{depositCount}</p>
            </div>
          </div>

          {toggleError && (
            <p role="alert" className="mt-4 text-sm text-destructive">
              {toggleError}
            </p>
          )}

          {/* Tabs Ativos/Inativos — a lista da API vem completa (inclui
              soft-deletados), quem separa é a tela. */}
          <div className="mt-6 flex w-fit gap-1 rounded-md border bg-muted p-1">
            <button
              type="button"
              onClick={() => setTab("ativos")}
              className={cn(
                // `min-h-9` no celular: `py-1` fechava em 28px.
                "inline-flex min-h-9 items-center rounded-sm px-3 py-1 text-sm sm:min-h-0",
                tab === "ativos"
                  ? "bg-card font-medium shadow-sm"
                  : "text-muted-foreground",
              )}
            >
              Ativos ({actives.length})
            </button>
            <button
              type="button"
              onClick={() => setTab("inativos")}
              className={cn(
                // `min-h-9` no celular: `py-1` fechava em 28px.
                "inline-flex min-h-9 items-center rounded-sm px-3 py-1 text-sm sm:min-h-0",
                tab === "inativos"
                  ? "bg-card font-medium shadow-sm"
                  : "text-muted-foreground",
              )}
            >
              Inativos ({inactives.length})
            </button>
          </div>

          {/* Services table */}
          {tab === "inativos" ? (
            <ServicosInativos establishmentId={establishmentId} services={inactives} />
          ) : (
            <>
              {/* Abaixo de `md` a tabela vira cartão: num telefone de 390 ela
                  pedia 220px de rolagem lateral, e quando você chegava no
                  switch e no "Editar" o NOME do serviço já tinha saído da
                  tela — sem saber qual serviço se estava desativando. */}
              <ul className="mt-4 space-y-3 md:hidden">
                {actives.map((s) => (
                  <li key={s.id} className="space-y-3 rounded-md border bg-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{s.name}</p>
                        <p className="text-xs text-muted-foreground">
                          no bot: {s.nickname}
                        </p>
                      </div>
                      <p className="shrink-0 text-sm font-medium">
                        {brl.format(s.price_cents / 100)}
                      </p>
                    </div>
                    <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      {s.duration_minutes} min
                      {s.deposit_required ? " · exige sinal" : ""}
                    </p>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Switch
                          checked={s.active}
                          disabled={togglingId === s.id}
                          onCheckedChange={() => setConfirmingOff(s)}
                          aria-label={`Desativar ${s.name}`}
                        />
                        Ativo
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 gap-1.5 px-2 text-xs sm:h-7"
                        onClick={() => {
                          setEditing(s);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                        Editar
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-4 hidden rounded-md border bg-card md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Serviço</TableHead>
                      <TableHead>Duração</TableHead>
                      <TableHead className="text-right">Preço</TableHead>
                      <TableHead className="text-center">Ativo</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {actives.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>
                          <p className="font-medium">{s.name}</p>
                          {/* O apelido é o que o cliente vê no WhatsApp — vale
                            conferir sem abrir a edição. */}
                          <p className="text-xs text-muted-foreground">
                            no bot: {s.nickname}
                          </p>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            {s.duration_minutes} min
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {brl.format(s.price_cents / 100)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={s.active}
                            disabled={togglingId === s.id}
                            onCheckedChange={() => setConfirmingOff(s)}
                            aria-label={`Desativar ${s.name}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 gap-1.5 px-2 text-xs sm:h-7"
                            onClick={() => {
                              setEditing(s);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {tab === "ativos" && actives.length === 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              {inactives.length === 0
                ? 'Nenhum serviço cadastrado ainda. Clique em "Novo Serviço" pra começar — sem serviço ativo o bot não tem o que oferecer.'
                : "Nenhum serviço ativo — os inativos estão na aba ao lado. Sem serviço ativo o bot não tem o que oferecer."}
            </p>
          )}

          <p className="mt-3 text-xs text-muted-foreground">
            Serviços inativos não aparecem para novos agendamentos, mas o histórico dos
            clientes é preservado. Desativar é reversível — o serviço fica na aba
            "Inativos" e pode ser reativado a qualquer momento.
          </p>
        </div>
      </div>

      <ResponsiveDialog
        open={confirmingOff !== null}
        onOpenChange={(open) => {
          if (!open && togglingId === null) setConfirmingOff(null);
        }}
      >
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              Desativar {confirmingOff?.name}?
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              O bot deixa de oferecer este serviço no WhatsApp e ele sai das opções de
              agendamento novo. Os agendamentos já marcados continuam de pé e o
              histórico é preservado. Ele fica na aba "Inativos" e volta com um clique.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmingOff(null)}
              disabled={togglingId !== null}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={onDeactivate}
              disabled={togglingId !== null}
            >
              {togglingId !== null ? "Desativando…" : "Desativar"}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <ServicoDialog
        establishmentId={establishmentId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        service={editing}
      />
    </>
  );
}
